import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { StyleClassModule } from 'primeng/styleclass';
import { filter, finalize } from 'rxjs';
import { HelpService } from '../../help/help.service';
import { LoginService } from '../../services/login.service';
import { SafraService } from '../../services/safra.service';
import { ToastService } from '../../services/toast.service';
import { AmbienteUsuario } from '../../types/LoginResponse.type';
import { Safra } from '../../types/Safra.type';
import { LayoutService } from '../service/layout.service';
import { AppConfigurator } from './app.configurator';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, SelectModule, StyleClassModule, AppConfigurator],
    template: ` <div class="layout-topbar" [class.with-help]="help.visible()">
        <div class="layout-topbar-logo-container">
            <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()" aria-label="Abrir menu">
                <i class="pi pi-bars"></i>
            </button>
        </div>

        <div class="layout-topbar-actions">
            @if (opcoesAmbiente().length > 1) {
                <p-select
                    class="layout-topbar-safra"
                    styleClass="layout-topbar-safra-select"
                    inputId="topbar-ambiente"
                    ariaLabel="Selecionar ambiente"
                    [options]="gruposAmbiente().length ? gruposAmbiente() : opcoesAmbiente()"
                    [group]="!!gruposAmbiente().length"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    [ngModel]="login.ambienteSelecionado()?.id"
                    (ngModelChange)="trocarAmbiente($event)"
                    optionLabel="nome"
                    optionValue="id"
                    [filter]="!!gruposAmbiente().length"
                    filterBy="nome,nomeCliente"
                    filterPlaceholder="Buscar cliente ou ambiente"
                    emptyFilterMessage="Nenhum ambiente encontrado"
                    appendTo="body"
                >
                    <ng-template #dropdownicon>
                        <i class="pi pi-database" aria-hidden="true"></i>
                    </ng-template>
                    <ng-template #selectedItem let-ambiente>
                        <span [title]="rotuloAmbiente(ambiente)">{{ rotuloAmbiente(ambiente) }}</span>
                    </ng-template>
                </p-select>
            }
            <p-select
                class="layout-topbar-safra"
                styleClass="layout-topbar-safra-select"
                inputId="topbar-safra"
                ariaLabel="Selecionar safra"
                [options]="safras"
                [ngModel]="safraSelecionadaId"
                (ngModelChange)="trocarSafra($event)"
                optionLabel="descricao"
                optionValue="id"
                placeholder="Selecionar safra"
                [loading]="carregandoSafras"
                [disabled]="carregandoSafras"
                [readonly]="!safras.length || !!erroSafras"
                [filter]="true"
                filterBy="descricao,safra"
                filterPlaceholder="Buscar safra"
                ariaFilterLabel="Buscar safra"
                [emptyMessage]="erroSafras || 'Nenhuma safra disponível'"
                emptyFilterMessage="Nenhuma safra encontrada"
                appendTo="body"
                (click)="abrirSeletorSafra()"
                (keydown)="aoTeclarSeletorSafra($event)"
            >
                <ng-template #dropdownicon>
                    <i class="pi pi-calendar" aria-hidden="true"></i>
                </ng-template>
                <ng-template #selectedItem let-safra>
                    <span [title]="safra.descricao">{{ safra.descricao }}</span>
                </ng-template>
            </p-select>
            <div class="relative">
                <button
                    type="button"
                    class="layout-topbar-action"
                    aria-label="Aparência"
                    title="Aparência"
                    pStyleClass="@next"
                    enterFromClass="hidden"
                    enterActiveClass="animate-scalein"
                    leaveToClass="hidden"
                    leaveActiveClass="animate-fadeout"
                    [hideOnOutsideClick]="true"
                >
                    <i class="pi pi-palette"></i>
                </button>
                <app-configurator />
            </div>
        </div>
    </div>`,
    styles: [`.layout-topbar.with-help { width: calc(100% - var(--layout-rail-width) - min(42rem, 46vw)); } @media (max-width: 991px) { .layout-topbar.with-help { width: calc(100% - min(42rem, 46vw)); } } @media (max-width: 900px) { .layout-topbar.with-help { width: 100%; } }`]
})
export class AppTopbar implements OnInit {
    safras: Safra[] = [];
    safraSelecionadaId: number | null = null;
    carregandoSafras = false;
    erroSafras = '';
    private readonly destroyRef = inject(DestroyRef);

    /** Ambientes (bases de dados) do usuário. */
    readonly opcoesAmbiente = computed(() => this.login.session()?.ambientes ?? []);

    /** Com mais de um cliente (admin do sistema), agrupa os ambientes por cliente. */
    readonly gruposAmbiente = computed(() => {
        const grupos = new Map<string, AmbienteUsuario[]>();
        for (const ambiente of this.opcoesAmbiente()) grupos.set(ambiente.nomeCliente, [...(grupos.get(ambiente.nomeCliente) ?? []), ambiente]);
        return grupos.size > 1 ? [...grupos].map(([label, items]) => ({ label, items })) : [];
    });

    constructor(public layoutService: LayoutService, public login: LoginService, private router: Router, public help: HelpService, private safraService: SafraService, private toastService: ToastService) {}

    ngOnInit(): void {
        this.atualizarAmbientes();
        this.carregarSafras();
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => this.sincronizarSafraSelecionada());
    }

    abrirSeletorSafra(): void {
        if (this.carregandoSafras) return;
        this.sincronizarSafraSelecionada();
        if (this.erroSafras || !this.safras.length) {
            this.toastService.erro('Safras indisponíveis', this.erroSafras || 'Nenhuma safra cadastrada.');
            this.carregarSafras();
        }
    }

    aoTeclarSeletorSafra(event: KeyboardEvent): void {
        if ((this.erroSafras || !this.safras.length) && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            this.abrirSeletorSafra();
        }
    }

    trocarSafra(id: number | null): void {
        if (!this.safras.some(safra => safra.id === id)) return;
        this.safraSelecionadaId = id;
        if (localStorage.getItem('safra-id') === String(id)) return;

        localStorage.setItem('safra-id', String(id));
        // As telas atuais recuperam a safra no ngOnInit; recarregar aplica o novo contexto.
        window.location.reload();
    }

    rotuloAmbiente(ambiente: AmbienteUsuario | null): string {
        if (!ambiente) return 'Selecionar ambiente';
        return this.gruposAmbiente().length ? `${ambiente.nomeCliente} · ${ambiente.nome}` : ambiente.nome;
    }

    trocarAmbiente(id: string | null): void {
        if (!id || id === this.login.ambienteSelecionado()?.id || !this.opcoesAmbiente().some(ambiente => ambiente.id === id)) return;

        this.login.selecionarAmbiente(id);
        // Os dados de todas as telas vêm da base do ambiente: recarregar aplica o novo contexto.
        this.recarregar();
    }

    /** Busca os ambientes atuais (chave regenerada ou ambiente novo desde o login). */
    private atualizarAmbientes(): void {
        if (!this.login.isAutenticado()) return;
        const chaveAnterior = this.login.getClientSecretKey();

        this.login.atualizarAmbientes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: ambientes => {
                if (!ambientes.length) {
                    this.toastService.alerta('Nenhum ambiente disponível', 'Seu cliente ainda não tem uma base de dados configurada. Procure o administrador.');
                } else if (chaveAnterior && chaveAnterior !== this.login.getClientSecretKey()) {
                    // A chave usada pelas telas já carregadas mudou: recarrega com a nova
                    this.recarregar();
                }
            },
            error: () => {}
        });
    }

    private carregarSafras(): void {
        if (this.carregandoSafras || !this.login.isAutenticado()) return;
        this.carregandoSafras = true;
        this.erroSafras = '';
        this.safraService.findAll().pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.carregandoSafras = false)
        ).subscribe({
            next: safras => {
                this.safras = [...safras].sort((a, b) => b.descricao.localeCompare(a.descricao, 'pt-BR'));
                this.sincronizarSafraSelecionada();
            },
            error: () => this.erroSafras = 'Não foi possível carregar as safras. Tente novamente.'
        });
    }

    private sincronizarSafraSelecionada(): void {
        const id = Number(localStorage.getItem('safra-id'));
        this.safraSelecionadaId = Number.isSafeInteger(id) && id > 0 && this.safras.some(safra => safra.id === id) ? id : null;
    }

    recarregar(): void { window.location.reload(); }

}
